import React, { useState, useEffect } from 'react';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { History, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';

interface ContratoVersionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  processoId: string;
  currentVersion: number;
}

type ContratoVersion = {
  id: string;
  versao: number | null;
  status: string;
  conteudo_html: string | null;
  created_at: string | null;
  aprovacoes: any;
};

export const ContratoVersionHistory: React.FC<ContratoVersionHistoryProps> = ({
  isOpen,
  onClose,
  processoId,
  currentVersion,
}) => {
  const { toast } = useToast();
  const [versions, setVersions] = useState<ContratoVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState<ContratoVersion | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadVersions();
    }
  }, [isOpen, processoId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contratos_gerados')
        .select('*')
        .eq('processo_id', processoId)
        .order('versao', { ascending: false });

      if (error) throw error;

      setVersions((data || []) as any);
    } catch (error) {
      console.error('Erro ao carregar versões:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o histórico de versões",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; icon: any; className: string }> = {
      rascunho: { label: 'Rascunho', icon: FileText, className: 'bg-gray-500' },
      em_revisao: { label: 'Em Revisão', icon: Clock, className: 'bg-blue-500' },
      aprovado: { label: 'Aprovado', icon: CheckCircle, className: 'bg-green-500' },
      rejeitado: { label: 'Rejeitado', icon: XCircle, className: 'bg-red-500' }
    };

    const config = statusConfig[status] || statusConfig.rascunho;
    const Icon = config.icon;
    
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getAprovacaoCount = (aprovacoes: any) => {
    if (!aprovacoes) return { aprovados: 0, total: 0 };
    
    const aprovadores = Object.values(aprovacoes);
    const total = aprovadores.length;
    const aprovados = aprovadores.filter((a: any) => a.status === 'aprovado').length;
    
    return { aprovados, total };
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Versões do Contrato
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {versions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma versão encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => {
                const { aprovados, total } = getAprovacaoCount(version.aprovacoes);
                const isCurrentVersion = version.versao === currentVersion;

                return (
                  <div
                    key={version.id}
                    className={`border rounded-lg p-4 ${
                      isCurrentVersion ? 'border-primary bg-primary/5' : 'border-border'
                    } ${
                      selectedVersion?.id === version.id ? 'ring-2 ring-primary' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">
                              Versão {version.versao}
                            </h3>
                            {isCurrentVersion && (
                              <Badge variant="outline" className="border-primary text-primary">
                                Atual
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {version.created_at ? new Date(version.created_at).toLocaleString('pt-BR') : 'Data não disponível'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {getStatusBadge(version.status)}
                        {total > 0 && (
                          <Badge variant="secondary">
                            {aprovados}/{total} aprovações
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant={selectedVersion?.id === version.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedVersion(
                          selectedVersion?.id === version.id ? null : version
                        )}
                      >
                        {selectedVersion?.id === version.id ? 'Ocultar' : 'Visualizar'}
                      </Button>
                    </div>

                    {selectedVersion?.id === version.id && (
                      <div className="mt-4 border-t pt-4">
                        <div className="bg-muted rounded-lg p-4 max-h-96 overflow-y-auto">
                          <div 
                            dangerouslySetInnerHTML={{ __html: version.conteudo_html || '<p>Conteúdo não disponível</p>' }}
                            className="prose prose-sm max-w-none"
                          />
                        </div>

                        {version.aprovacoes && Object.keys(version.aprovacoes).length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-semibold mb-2">Aprovadores:</h4>
                            <div className="space-y-2">
                              {Object.entries(version.aprovacoes).map(([key, aprovador]: [string, any]) => (
                                <div key={key} className="flex items-center justify-between p-2 bg-background rounded">
                                  <span className="text-sm">{aprovador.nome}</span>
                                  <Badge variant={
                                    aprovador.status === 'aprovado' ? 'default' : 
                                    aprovador.status === 'rejeitado' ? 'destructive' : 
                                    'secondary'
                                  }>
                                    {aprovador.status === 'aprovado' ? 'Aprovado' : 
                                     aprovador.status === 'rejeitado' ? 'Rejeitado' : 
                                     'Pendente'}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};